<template>
  <div class="panel-fragment">
    <div class="panel-content is-full-sized">
      <!-- See Maps.vue for the layout rationale. -->
      <div class="panel-header">
        <h1>
          <strong>{{ totalScenarios }}</strong> {{ $t('page.create.scenarios.header_unit') }}
        </h1>
      </div>

      <div class="forge-controls">
        <div class="forge-toolbar">
          <input
            type="text"
            class="forge-search"
            :placeholder="$t('page.create.common.search_placeholder')"
            :aria-label="$t('page.create.common.search_placeholder')"
            v-model="filters.name"
            @input="onFilterInput" />

          <select
            class="forge-size-filter"
            v-model="filters.size"
            :aria-label="$t('a11y_portal.filter_size')"
            @change="onFilterChange">
            <option value="">{{ $t('page.create.common.size_any') }}</option>
            <option
              v-for="size in sizeChoices"
              :key="size"
              :value="size">
              {{ $t(`map.size.${size}.label`) }}
            </option>
          </select>

          <select
            class="forge-size-filter"
            v-model="filters.speed"
            :aria-label="$t('a11y_portal.filter_speed')"
            @change="onFilterChange">
            <option value="">{{ $t('page.create.scenarios.speed_any') }}</option>
            <option
              v-for="speed in speedChoices"
              :key="speed"
              :value="speed">
              {{ $t(`data.speed.${speed}.name`) }}
            </option>
          </select>

          <select
            class="forge-size-filter"
            v-model="filters.factions"
            :aria-label="$t('a11y_portal.filter_factions')"
            @change="onFilterChange">
            <option value="">{{ $t('page.create.scenarios.factions_any') }}</option>
            <option
              v-for="n in factionChoices"
              :key="n"
              :value="n">
              {{ n }}
            </option>
          </select>

          <select
            class="forge-size-filter"
            v-model="filters.sort"
            :aria-label="$t('a11y_portal.sort_by')"
            @change="onFilterChange">
            <option
              v-for="opt in sortOptions"
              :key="opt"
              :value="opt">
              {{ $t(`page.create.common.sort.${opt}`) }}
            </option>
          </select>
        </div>

        <div class="forge-chips">
          <button
            v-for="chip in chipChoices"
            :key="chip"
            class="forge-chip"
            :class="{ 'is-active': activeChip === chip }"
            :aria-pressed="activeChip === chip ? 'true' : 'false'"
            @click="setChip(chip)">
            {{ $t(`page.create.common.chip.${chip}`) }}
          </button>
        </div>
      </div>
      <!-- /forge-controls -->

      <v-scrollbar
        v-if="loaded"
        class="content">
        <div
          v-if="scenarios.length === 0"
          class="full-sized-text">
          {{ $t('page.create.scenarios.no_results') }}
        </div>
        <template v-else>
          <table class="default-table scenarios-table forge-cards">
            <tr
              v-for="scenario in scenarios"
              :key="scenario.id">
              <td class="forge-card-thumb">
                <router-link
                  :to="`/create/scenario/view/${scenario.share_token}`"
                  tabindex="-1"
                  aria-hidden="true">
                  <img
                    v-if="scenario.thumbnail"
                    :src="scenario.thumbnail"
                    :alt="scenario.game_metadata.name" />
                  <div
                    v-else
                    class="forge-card-thumb-placeholder">
                    <svgicon name="galaxy" aria-hidden="true" />
                  </div>
                </router-link>
              </td>
              <td class="forge-card-body">
                <h2>
                  <router-link :to="`/create/scenario/view/${scenario.share_token}`">
                    {{ scenario.game_metadata.name }}
                  </router-link>
                </h2>
                <em>
                  {{ $t(`map.size.${scenario.game_metadata.size}.toast`) }},
                  {{ scenario.game_metadata.factions.length }} factions,
                  {{ $t(`data.speed.${scenario.game_metadata.speed}.name`) }},
                  {{ scenario.plays || 0 }} {{ $t('page.create.common.plays') }}
                  <span
                    class="toast"
                    v-if="!scenario.author && scenario.is_official">
                    {{ $t('page.create.scenarios.official') }}
                  </span>
                  <span
                    class="toast"
                    v-else-if="scenario.author">
                    {{ $t('page.create.common.by') }} {{ scenario.author.name }}
                  </span>
                  <span
                    class="toast"
                    v-if="!scenario.published_at">
                    {{ $t('page.create.common.draft') }}
                  </span>
                  <span
                    v-for="mut in mutatorsOf(scenario)"
                    :key="mut.key"
                    class="toast mutator-chip"
                    :title="$t(`data.mutator.${mut.key}.description`)">
                    {{ $t(`data.mutator.${mut.key}.name`) }}
                  </span>
                </em>
                <div class="reactions row-reactions">
                  <button
                    class="reaction-button"
                    v-tooltip="$t('page.create.common.like')"
                    :aria-label="`${$t('page.create.common.like')}: ${scenario.likes || 0}`"
                    @click="react(scenario, 'likes')">
                    <svgicon name="check" aria-hidden="true" />{{ scenario.likes || 0 }}
                  </button>
                  <button
                    class="reaction-button"
                    v-tooltip="$t('page.create.common.dislike')"
                    :aria-label="`${$t('page.create.common.dislike')}: ${scenario.dislikes || 0}`"
                    @click="react(scenario, 'dislikes')">
                    <svgicon name="close" aria-hidden="true" />{{ scenario.dislikes || 0 }}
                  </button>
                  <button
                    class="reaction-button"
                    v-tooltip="$t('page.create.common.favorite')"
                    :aria-label="`${$t('page.create.common.favorite')}: ${scenario.favorites || 0}`"
                    @click="react(scenario, 'favorites')">
                    <svgicon name="bookmark" aria-hidden="true" />{{ scenario.favorites || 0 }}
                  </button>
                </div>
              </td>
              <td class="actions">
                <div class="forge-card-actions">
                  <router-link
                    class="default-button squared"
                    v-tooltip="$t('page.create.common.view')"
                    :aria-label="$t('page.create.common.view')"
                    :to="`/create/scenario/view/${scenario.share_token}`">
                    <svgicon name="eye" aria-hidden="true" />
                  </router-link>
                  <router-link
                    class="default-button squared"
                    v-tooltip="$t('page.create.scenarios.edit')"
                    :aria-label="$t('page.create.scenarios.edit')"
                    :to="`/create/scenario/edit/${scenario.id}`">
                    <svgicon name="pencil" aria-hidden="true" />
                  </router-link>
                  <button
                    class="default-button squared"
                    v-tooltip="$t('page.create.common.share_tooltip')"
                    :aria-label="$t('page.create.common.share')"
                    @click="share(scenario)">
                    <svgicon name="share" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          </table>

          <div
            v-if="totalPages > 1"
            class="forge-pagination">
            <button
              class="default-button"
              :disabled="page <= 1"
              @click="goToPage(page - 1)">
              <svgicon name="caret-left" aria-hidden="true" /> {{ $t('page.create.common.previous') }}
            </button>
            <span class="forge-pagination-info">
              {{ $t('page.create.common.page_of', { current: page, total: totalPages }) }}
            </span>
            <button
              class="default-button"
              :disabled="page >= totalPages"
              @click="goToPage(page + 1)">
              {{ $t('page.create.common.next') }} <svgicon name="caret-right" aria-hidden="true" />
            </button>
          </div>
        </template>

        <hr class="margin">
      </v-scrollbar>
      <loading-mask v-else />
    </div>

    <v-scrollbar class="panel-aside">
      <div class="panel-aside-info">
        <h2>{{ $t('page.create.scenarios.about_heading') }}</h2>
        <p v-html="$t('page.create.scenarios.about_body')"></p>
      </div>
      <div class="panel-aside-info">
        <h2>{{ $t('page.create.scenarios.roadmap_heading') }}</h2>
        <p v-html="$t('page.create.scenarios.roadmap_body')"></p>
      </div>
      <hr class="margin">
    </v-scrollbar>
  </div>
</template>

<script>
import config from '@/config';
import { copyToClipboard } from '@/utils/clipboard';

import Loading from '@/portal/mixins/Loading';

import LoadingMask from '@/portal/components/LoadingMask.vue';

export default {
  name: 'create-scenarios',
  mixins: [Loading],
  data() {
    return {
      scenarios: [],
      totalScenarios: 0,
      totalPages: 1,
      page: 1,
      // key → {name, description}. Lets the row chips render
      // flavorful names instead of raw atom keys without coupling
      // the row template to the backend catalog response shape.
      mutatorIndex: {},
      sizeChoices: [80, 120, 200, 360, 500, 750],
      speedChoices: ['fast', 'medium', 'slow'],
      // Maps wizard caps at 8 factions; surfacing 2-8 covers every
      // valid scenario without dragging in counts the UI can't produce.
      factionChoices: [2, 3, 4, 5, 6, 7, 8],
      sortOptions: ['newest', 'most_liked', 'most_favorited', 'most_played'],
      chipChoices: ['all', 'officials', 'mine', 'favorited', 'drafts'],
      activeChip: 'all',
      filters: {
        name: '',
        size: '',
        speed: '',
        factions: '',
        sort: 'newest',
      },
      debouncedReload: null,
    };
  },
  methods: {
    async loadData() {
      const params = { page: this.page, sort: this.filters.sort };
      if (this.filters.name) params.name = this.filters.name;
      if (this.filters.size) params.size = this.filters.size;
      if (this.filters.speed) params.speed = this.filters.speed;
      if (this.filters.factions) params.factions = this.filters.factions;
      if (this.activeChip !== 'all') params[this.activeChip] = 'true';

      const resp = await this.releaseLoading(this.$axios.get('/scenarios', { params }));
      this.scenarios = resp.data;
      this.totalScenarios = parseInt(resp.headers.total, 10) || 0;
      this.totalPages = parseInt(resp.headers['total-pages'], 10) || 1;
    },
    setChip(chip) {
      this.activeChip = this.activeChip === chip ? 'all' : chip;
      this.page = 1;
      this.loadData();
    },
    onFilterInput() {
      this.page = 1;
      this.debouncedReload();
    },
    onFilterChange() {
      this.page = 1;
      this.loadData();
    },
    goToPage(target) {
      if (target < 1 || target > this.totalPages) return;
      this.page = target;
      this.loadData();
    },
    async react(scenario, kind) {
      try {
        await this.$axios.post(`/scenarios/${scenario.id}/folders/${kind}`);
        this.$set(scenario, kind, (scenario[kind] || 0) + 1);
      } catch (err) {
        this.$toastError(this.$t('page.create.common.error_generic'));
      }
    },
    async share(scenario) {
      // The /forge URL (not the SPA route) so the link unfurls with a
      // title + thumbnail when pasted on Discord etc.
      const copied = await copyToClipboard(`${config.BASE_URL}/forge/scenario/${scenario.share_token}`);
      if (copied) {
        this.$toasted.success(this.$t('page.create.common.share_copied'));
      } else {
        this.$toastError(this.$t('page.create.common.error_generic'));
      }
    },
    // Returns the catalog entries (with display names) for the
    // mutators a scenario has active. Falls back to the raw key
    // when the catalog hasn't loaded yet so chips don't disappear.
    mutatorsOf(scenario) {
      const game = scenario.game_metadata && scenario.game_metadata.mutators;
      const list = Array.isArray(game) ? game : [];
      return list.map((m) => this.mutatorIndex[m.key] || { key: m.key, name: m.key });
    },
  },
  created() {
    this.debouncedReload = this._.debounce(this.loadData, 300);
  },
  mounted() {
    this.loadData();
    // Fetch once for chip names.
    this.$axios.get('/data/mutators').then(({ data }) => {
      this.mutatorIndex = data.reduce((acc, m) => { acc[m.key] = m; return acc; }, {});
    }).catch(() => {});
  },
  components: {
    LoadingMask,
  },
};
</script>
